// The soundtrack: four pieces written as bars of notes, each in layers that the music director brings in and out
// with the intensity of the moment (0: a pad and an arpeggio; 3: everything). All in the family of D major, the key
// of the bells and fanfares of the sound bank, so a lap bell over the race music is in tune. Pure data and pure
// functions: a bar is the same on every machine, and the tests read every note.
import { chord as ch, hash, spread, type ScaleName } from './theory.js';

export type SongId = 'dawn' | 'peloton' | 'tempo' | 'podium';
export type Inst = 'kick' | 'snare' | 'clap' | 'hatC' | 'hatO' | 'shaker' | 'rim' | 'crash' | 'riser' | 'bass' | 'pad' | 'keys' | 'pluck' | 'lead';
export type LayerName = 'pad' | 'arp' | 'drums' | 'perc' | 'bass' | 'lead' | 'fx';
export const LAYERS: readonly LayerName[] = ['pad', 'arp', 'drums', 'perc', 'bass', 'lead', 'fx'];
/** Drums carry no pitch; `n` is 0 for them. */
export const DRUMS: readonly Inst[] = ['kick', 'snare', 'clap', 'hatC', 'hatO', 'shaker', 'rim', 'crash', 'riser'];

/** One note of a bar: start and length in beats (a bar has four), MIDI pitch, velocity 0-1. */
export interface Note { inst: Inst; t: number; dur: number; n: number; vel: number }
/** What a layer knows when it writes its bar. */
export interface BarCtx {
  bar: number;
  /** The bar's chord voicing (MIDI) and its bass note. */
  chord: readonly number[]; bass: number;
  /** Section of the form (0: A, 1: B…) and whether this bar ends an eight-bar phrase (a fill). */
  section: number; fill: boolean;
  /** 0-3: the director's intensity; a layer may thicken with it. */
  intensity: number;
  /** Deterministic unit values for this bar: `rnd(i)` is the same every time this bar plays. */
  rnd: (i: number) => number;
}
export interface Layer {
  /** Lowest intensity at which the layer plays. */
  from: number;
  draw: (c: BarCtx) => Note[];
}
export interface Chord { notes: readonly number[]; bass: number }
export interface Song {
  id: SongId;
  bpm: number;
  /** Key: root pitch class (MIDI, any octave) and scale; every pitched note of the song is in it (tested). */
  root: number; scale: ScaleName;
  /** Bars of one loop and of one section of the form. */
  bars: number; section: number;
  /** Offbeat sixteenths are played this late (fraction of a sixteenth): 0 straight, 0.3 a light shuffle. */
  swing: number;
  /** One chord per bar of the loop. */
  progression: readonly Chord[];
  layers: Partial<Record<LayerName, Layer>>;
}

// ---- writing helpers ----
const note = (inst: Inst, t: number, dur: number, n: number, vel: number): Note => ({ inst, t, dur, n, vel });
/** Sixteenth-step pattern: `X` accent, `x` hit, `o` soft, `.` rest. */
function steps(inst: Inst, pattern: string, vel = 0.8, dur = 0.25): Note[] {
  const out: Note[] = [];
  for (let i = 0; i < pattern.length; i++) {
    const c = pattern[i];
    if (c === '.') continue;
    out.push(note(inst, i / 4, dur, 0, vel * (c === 'X' ? 1 : c === 'x' ? 0.78 : 0.5)));
  }
  return out;
}
/** A melody line: [start, length, pitch] triples. */
const line = (inst: Inst, vel: number, notes: readonly (readonly [number, number, number])[]): Note[] => notes.map(([t, dur, n]) => note(inst, t, dur, n, vel));
/** An arpeggio of `inst` over the chord's spread, one note per `step` beats, following `order` indices. */
function arp(inst: Inst, c: BarCtx, oct: number, order: readonly number[], step: number, vel: number, accentEvery: number, skip = 0): Note[] {
  const mat = spread(c.chord.map(x => x + 12 * oct), Math.max(...order) + 1), out: Note[] = [];
  order.forEach((idx, i) => {
    if (skip > 0 && i > 0 && c.rnd(40 + i) < skip) return;
    out.push(note(inst, i * step, step * 0.9, mat[idx], i % accentEvery === 0 ? vel : vel * 0.68));
  });
  return out;
}
const chordNotes = (inst: Inst, c: BarCtx, t: number, dur: number, vel: number): Note[] => c.chord.map(n => note(inst, t, dur, n, vel));
const C = (root: number, intervals: readonly number[], bass: number): Chord => ({ notes: ch(root, intervals), bass });
const ROOT = { D: 62, A: 57, B: 59, G: 55, Fs: 54, E: 52 };

// ---- Dawn: the menu. A slow sunrise over the first kilometres: electric piano, a plucked arpeggio, a brushed beat. ----
const Dmaj7 = C(ROOT.D, [0, 4, 7, 11], 38), Bm7 = C(ROOT.B, [0, 3, 7, 10], 47), Gmaj7 = C(ROOT.G, [0, 4, 7, 11], 43), Aadd9 = C(ROOT.A, [0, 4, 7, 14], 45), Fsm7 = C(ROOT.Fs, [0, 3, 7, 10], 42);
const dawn: Song = {
  id: 'dawn', bpm: 92, root: 62, scale: 'major', bars: 16, section: 8, swing: 0.12,
  progression: [Dmaj7, Dmaj7, Bm7, Bm7, Gmaj7, Gmaj7, Aadd9, Aadd9, Dmaj7, Dmaj7, Fsm7, Fsm7, Gmaj7, Gmaj7, Aadd9, Aadd9],
  layers: {
    pad: { from: 0, draw: c => [...chordNotes('keys', c, 0, 3.6, 0.55), ...(c.bar % 2 ? chordNotes('keys', c, 3.5, 0.5, 0.32) : []), ...chordNotes('pad', c, 0, 4, 0.3)] },
    arp: { from: 1, draw: c => arp('pluck', c, 1, [0, 2, 4, 5, 4, 2, 1, 3], 0.5, 0.6, 4, 0.12) },
    bass: { from: 1, draw: c => [note('bass', 0, 1.5, c.bass, 0.7), ...(c.bar % 2 ? [note('bass', 2.5, 0.5, c.bass + 7, 0.5)] : []), note('bass', 3.5, 0.5, c.bass, 0.6)] },
    drums: {
      from: 1, draw: c => [
        ...steps('kick', 'X.......x.......', 0.55), ...steps('shaker', 'o.x.o.x.o.x.o.X.', 0.5, 0.2),
        ...(c.bar % 2 ? steps('rim', '..........x.....', 0.35) : []), ...(c.fill ? steps('hatO', '..............x.', 0.4, 0.5) : []),
      ],
    },
    // a two-bar answer high on the strings, in the second half of the form only
    lead: {
      from: 2, draw: c => (c.section !== 1 ? [] : c.bar % 2 === 0 ? line('pluck', 0.5, [[0, 1, 81], [1, 1.5, 78], [2.5, 0.5, 74], [3, 1, 76]]) : line('pluck', 0.5, [[0, 2, 78], [2, 2, 76]])),
    },
  },
};

// ---- Peloton: the race. Driving house in B minor: a four-to-the-floor kick, an off-beat bass, a hook on the lead, a breakdown and a riser back into it. ----
const Bm = C(ROOT.B, [0, 3, 7, 10], 47), G6 = C(ROOT.G, [0, 4, 7, 11], 43), DA = C(ROOT.A, [0, 5, 9, 12], 38), A = C(ROOT.A, [0, 4, 7, 12], 45), Fsm = C(ROOT.Fs, [0, 3, 7, 12], 42);
const HOOK: readonly (readonly (readonly [number, number, number])[])[] = [
  [[0, 0.75, 83], [0.75, 0.25, 81], [1, 1, 78], [2.5, 0.5, 74], [3, 0.5, 76], [3.5, 0.5, 78]],
  [[0, 1.5, 79], [1.5, 0.5, 78], [2, 1, 76], [3, 1, 74]],
  [[0, 0.5, 74], [0.5, 0.5, 78], [1, 1.5, 81], [2.5, 0.5, 83], [3, 1, 81]],
  [[0, 1, 78], [1, 0.5, 76], [1.5, 1.5, 73]],
  [[0, 0.75, 83], [0.75, 0.25, 81], [1, 1, 78], [2.5, 0.5, 74], [3, 0.5, 76], [3.5, 0.5, 78]],
  [[0, 1, 79], [1, 1, 83], [2, 1, 81], [3, 1, 79]],
  [[0, 1.5, 78], [1.5, 0.5, 74], [2, 2, 69]],
  [[0, 1, 73], [1, 1, 76], [2, 2, 78]],
];
/** The last bar of the second A, leading up into the breakdown. */
const HOOK_UP: readonly (readonly [number, number, number])[] = [[0, 1, 73], [1, 1, 76], [2, 2, 81]];
/** The four bars that bring the hook back after the breakdown. */
const PICKUP: readonly (readonly (readonly [number, number, number])[])[] = [[[0, 2, 79], [2, 2, 81]], [[0, 4, 83]], [[0, 1.5, 81], [1.5, 1, 78], [2.5, 1.5, 74]], [[0, 2, 76], [2, 2, 78]]];
const peloton: Song = {
  id: 'peloton', bpm: 128, root: 59, scale: 'minor', bars: 32, section: 16, swing: 0,
  progression: [Bm, G6, DA, A, Bm, G6, DA, Fsm, Bm, G6, DA, A, Bm, G6, DA, A, G6, A, Bm, Bm, G6, A, DA, DA, G6, A, Bm, Fsm, G6, A, DA, A],
  layers: {
    // the breakdown stabs its chords; the rest of the time the pad holds them
    pad: { from: 0, draw: c => (c.section === 1 && c.intensity >= 2 ? [0, 1.5, 3].flatMap(t => chordNotes('pad', c, t, 0.55, 0.7)) : chordNotes('pad', c, 0, 4, 0.5)) },
    arp: { from: 0, draw: c => (c.intensity === 0 ? arp('pluck', c, 1, [0, 4, 1, 5, 0, 4, 2, 6], 0.5, 0.55, 4) : arp('pluck', c, 1, [0, 2, 4, 2, 1, 3, 5, 3, 0, 2, 4, 6, 5, 3, 2, 1], 0.25, 0.6, 4)) },
    drums: {
      from: 1, draw: c => [
        ...steps('kick', c.fill ? 'X...X...X.X.X.X.' : 'X...X...X...X...', 0.95),
        ...(c.intensity >= 2 ? steps('hatC', 'oxoXoxoXoxoXoxoX', 0.6, 0.12) : steps('hatC', '..x...x...x...x.', 0.6, 0.12)),
        ...(c.intensity >= 3 ? steps('hatO', '..x...x...x...x.', 0.45, 0.4) : []),
      ],
    },
    perc: {
      from: 2, draw: c => [
        ...steps('clap', '....X.......X...', 0.8), ...steps('rim', '...o.....o....o.', 0.4, 0.1),
        ...(c.bar % 16 === 15 ? steps('snare', '........x.x.xxxX', 0.7) : c.fill ? steps('snare', '..............xX', 0.6) : []),
      ],
    },
    bass: {
      from: 1, draw: c => {
        const r = c.bass;
        if (c.section === 1) return [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5].map((t, i) => note('bass', t, 0.4, i % 4 === 3 ? r + 12 : r, i % 2 ? 0.7 : 0.85));
        if (c.intensity >= 2) return [[0, r, 0.9], [0.5, r, 0.7], [1.25, r + 12, 0.6], [1.5, r, 0.8], [2, r, 0.9], [2.5, r, 0.7], [3.25, r + 12, 0.6], [3.5, r, 0.8]].map(([t, n, v]) => note('bass', t, 0.35, n, v));
        return [0.5, 1.5, 2.5, 3.5].map(t => note('bass', t, 0.4, r, 0.8));
      },
    },
    lead: {
      from: 2, draw: c => {
        const b = c.bar % 32;
        const phrase = b < 8 ? HOOK[b] : b < 16 ? (b === 15 ? HOOK_UP : HOOK[b - 8]) : b >= 28 ? PICKUP[b - 28] : null;
        if (!phrase) return [];
        const main = line('lead', 0.8, phrase);
        // flat out, the hook is doubled an octave up on the strings
        return c.intensity >= 3 ? [...main, ...phrase.map(([t, dur, n]) => note('pluck', t, dur, n + 12, 0.35))] : main;
      },
    },
    fx: { from: 2, draw: c => [...(c.bar % 16 === 0 ? [note('crash', 0, 2, 0, 0.6)] : []), ...(c.bar % 16 === 14 ? [note('riser', 0, 8, 0, 0.6)] : [])] },
  },
};

// ---- Tempo: workouts and free rides. Steady, dark and rolling in E Dorian: a tight kick, sixteenths on the hats, a rolling bass that thickens with the effort. ----
const Em7 = C(ROOT.E, [0, 3, 7, 10], 40), G = C(ROOT.G, [0, 4, 7, 12], 43), A7 = C(ROOT.A, [0, 4, 7, 10], 45), D = C(50, [0, 7, 12, 16], 38);
const tempo: Song = {
  id: 'tempo', bpm: 124, root: 52, scale: 'dorian', bars: 16, section: 8, swing: 0,
  progression: [Em7, Em7, G, G, A7, A7, Em7, Em7, Em7, Em7, G, G, D, D, A7, A7],
  layers: {
    pad: { from: 0, draw: c => chordNotes('pad', c, 0, 4, 0.65) },
    arp: { from: 1, draw: c => arp('pluck', c, 1, [0, 2, 1, 3, 0, 2, 1, 3], 0.5, 0.55, 4) },
    drums: { from: 1, draw: c => [...steps('kick', c.fill ? 'X...X...X...X.X.' : 'X...X...X...X...', 0.95), ...steps('hatC', 'oxoxoxoxoxoxoxox', 0.55, 0.12)] },
    perc: { from: 2, draw: () => [...steps('clap', '....X.......X...', 0.75), ...steps('rim', '...o..o....o..o.', 0.4, 0.1), ...steps('hatO', '..x...x...x...x.', 0.5, 0.4)] },
    bass: {
      from: 1, draw: c => {
        const r = c.bass;
        if (c.intensity < 2) return [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5].map((t, i) => note('bass', t, 0.4, r, i % 4 === 0 ? 0.9 : 0.65));
        return Array.from({ length: 16 }, (_, i) => note('bass', i / 4, 0.2, i % 8 === 3 ? r + 12 : i % 8 === 7 ? r + 7 : r, i % 4 === 0 ? 0.9 : 0.6));
      },
    },
    lead: {
      from: 2, draw: c => {
        const stabs = c.bar % 2 === 0 ? [[1.5, 0.3], [3, 0.3]] : [[0.5, 0.3], [2.5, 0.8]];
        const out = stabs.flatMap(([t, d]) => c.chord.slice(1).map(n => note('keys', t, d, n, 0.6)));
        return c.intensity >= 3 ? [...out, ...line('pluck', 0.5, [[0, 0.5, 76], [0.75, 0.5, 79], [1.5, 0.5, 81], [2.25, 1.5, 83]])] : out;
      },
    },
    fx: { from: 3, draw: c => [...(c.bar % 16 === 0 ? [note('crash', 0, 2, 0, 0.5)] : []), ...(c.bar % 16 === 14 ? [note('riser', 0, 8, 0, 0.5)] : [])] },
  },
};

// ---- Podium: the results. Bright and unhurried in D major: electric piano chords, a plucked arpeggio, a short triumphant line. ----
const Dp = C(ROOT.A, [0, 5, 9, 12], 38), Ap = C(ROOT.A, [0, 4, 7, 12], 45), Bmp = C(ROOT.B, [0, 3, 7, 12], 47), Gp = C(ROOT.G, [0, 4, 7, 12], 43);
const FANFARE: readonly (readonly (readonly [number, number, number])[])[] = [
  [[0, 0.5, 74], [0.5, 0.5, 76], [1, 1, 78], [2, 2, 81]],
  [[0, 1.5, 73], [1.5, 0.5, 81], [2, 2, 76]],
  [[0, 1, 78], [1, 1, 74], [2, 2, 71]],
  [[0, 1.5, 79], [1.5, 0.5, 81], [2, 1, 83], [3, 1, 81]],
];
const podium: Song = {
  id: 'podium', bpm: 112, root: 62, scale: 'major', bars: 8, section: 8, swing: 0.08,
  progression: [Dp, Ap, Bmp, Gp, Dp, Ap, Gp, Ap],
  layers: {
    pad: { from: 0, draw: c => [...chordNotes('keys', c, 0, 3.5, 0.6), ...chordNotes('pad', c, 0, 4, 0.3)] },
    arp: { from: 1, draw: c => arp('pluck', c, 1, [0, 2, 4, 2, 1, 3, 5, 3], 0.5, 0.55, 4) },
    drums: { from: 1, draw: () => [...steps('kick', 'X.......X.......', 0.7), ...steps('shaker', 'x.x.x.x.x.x.x.x.', 0.5, 0.2)] },
    perc: { from: 2, draw: () => [...steps('clap', '....X.......X...', 0.7), ...steps('hatO', '......x.......x.', 0.4, 0.4)] },
    bass: { from: 1, draw: c => [note('bass', 0, 1.5, c.bass, 0.7), note('bass', 2.5, 0.5, c.bass, 0.5), note('bass', 3, 1, c.bass + 7, 0.55)] },
    lead: { from: 2, draw: c => line('lead', 0.7, c.bar % 8 === 7 ? [[0, 2, 76], [2, 2, 78]] : FANFARE[c.bar % 4]) },
    fx: { from: 2, draw: c => (c.bar % 8 === 0 ? [note('crash', 0, 2, 0, 0.5)] : []) },
  },
};

export const SONGS: Record<SongId, Song> = { dawn, peloton, tempo, podium };
export const SONG_IDS = Object.keys(SONGS) as SongId[];

/** Highest intensity a director may ask for. */
export const MAX_INTENSITY = 3;

/** Seconds of one beat and of one bar of a song. */
export const beatLen = (s: Song): number => 60 / s.bpm;
export const barLen = (s: Song): number => beatLen(s) * 4;

/** Context of bar `bar` of a song at an intensity. */
export function barContext(song: Song, bar: number, intensity: number): BarCtx {
  const b = ((bar % song.bars) + song.bars) % song.bars, c = song.progression[b], id = SONG_IDS.indexOf(song.id);
  return { bar: b, chord: c.notes, bass: c.bass, section: Math.floor(b / song.section), fill: b % 8 === 7, intensity: Math.max(0, Math.min(MAX_INTENSITY, Math.floor(intensity))), rnd: i => hash(id, b, i) };
}

/** A note with the layer that wrote it (the bus it plays on). */
export interface PlacedNote extends Note { layer: LayerName }

/**
 * Every note of bar `bar` at `intensity`: the layers that play at that intensity, in time order, swung and lightly
 * humanised (velocity within ±6 %, the same way every time the bar comes round).
 */
export function barNotes(song: Song, bar: number, intensity: number): PlacedNote[] {
  const c = barContext(song, bar, intensity), out: PlacedNote[] = [];
  let i = 0;
  for (const layer of LAYERS) {
    const L = song.layers[layer];
    if (!L || L.from > c.intensity) continue;
    for (const nt of L.draw(c)) {
      const off = song.swing && Math.round(nt.t * 4) % 2 === 1 ? song.swing * 0.25 : 0;
      out.push({ ...nt, layer, t: nt.t + off, vel: Math.max(0.05, Math.min(1, nt.vel * (0.94 + 0.12 * c.rnd(1000 + i++)))) });
    }
  }
  return out.sort((a, b) => a.t - b.t);
}

/** Layers of a song that play at an intensity. */
export const activeLayers = (song: Song, intensity: number): LayerName[] => LAYERS.filter(l => song.layers[l] && song.layers[l]!.from <= intensity);
